import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useLanguage } from '../context/LanguageContext';
import { verificationAPI, STORAGE_URL } from '../services/api';

// Helper: Convert English numerals to Marathi
const toMarathiNumerals = (num) => {
    if (num === null || num === undefined || num === '') return '-';
    const marathiDigits = ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'];
    return String(num).replace(/[0-9]/g, (d) => marathiDigits[parseInt(d)]);
};

// Helper: Format date as dd/mm/yyyy in Marathi numerals
const toMarathiDateShort = (dateStr) => {
    if (!dateStr) return '-';
    try {
        const d = new Date(dateStr);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return toMarathiNumerals(`${day}/${month}/${year}`);
    } catch {
        return '-';
    }
};

// Helper: Format date as dd/mm/yyyy
const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
        const d = new Date(dateStr);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
    } catch {
        return '-';
    }
};

// Helper: Check if text is already in Devanagari
const isDevanagari = (text) => {
    if (!text) return false;
    return /[ऀ-ॿ]/.test(text);
};

// Helper: Get Marathi text (prefer _mr field, fallback to transliteration)
const getMarathiText = (cert, field) => {
    const marathiField = `${field}_mr`;
    if (cert[marathiField] && cert[marathiField].trim() !== '') {
        return cert[marathiField];
    }
    return transliterateToMarathi(cert[field] || '');
};

// Helper: Transliterate common names/words to Marathi
const transliterateToMarathi = (text) => {
    if (!text) return '';
    if (isDevanagari(text)) return text; // Already in Marathi

    // Common Maharashtrian names dictionary
    const commonNames = {
        // Male names
        'Rajesh': 'राजेश', 'Suresh': 'सुरेश', 'Mahesh': 'महेश', 'Dinesh': 'दिनेश',
        'Ramesh': 'रमेश', 'Ganesh': 'गणेश', 'Mangesh': 'मंगेश', 'Sandesh': 'संदेश',
        'Prakash': 'प्रकाश', 'Vikram': 'विक्रम', 'Sachin': 'सचिन', 'Rahul': 'राहुल',
        'Amit': 'अमित', 'Sunil': 'सुनील', 'Anil': 'अनिल', 'Vijay': 'विजय',
        'Sanjay': 'संजय', 'Deepak': 'दीपक', 'Dipak': 'दिपक', 'Nilesh': 'निलेश',
        'Datta': 'दत्ता', 'Eknath': 'एकनाथ', 'Krishna': 'कृष्णा', 'Gopal': 'गोपाळ',
        'Shiva': 'शिवा', 'Shankar': 'शंकर', 'Vishnu': 'विष्णू', 'Mohan': 'मोहन',
        'Rohan': 'रोहन', 'Sohan': 'सोहन', 'Ashok': 'अशोक', 'Vasant': 'वसंत',
        'Kiran': 'किरण', 'Shree': 'श्री', 'Shri': 'श्री', 'Shreyas': 'श्रेयस',
        'Sagar': 'सागर', 'Ravi': 'रवि', 'Ajay': 'अजय', 'Vivek': 'विवेक',
        'Prasad': 'प्रसाद', 'Umesh': 'उमेश', 'Santosh': 'संतोष',
        'Anand': 'आनंद', 'Mukesh': 'मुकेश', 'Rakesh': 'राकेश', 'Naresh': 'नरेश',
        'Paresh': 'परेश', 'Bhavesh': 'भावेश', 'Dhiren': 'धीरेन', 'Jiten': 'जितेन',
        // Female names
        'Priya': 'प्रिया', 'Pooja': 'पूजा', 'Puja': 'पूजा', 'Sneha': 'स्नेहा',
        'Neha': 'नेहा', 'Rekha': 'रेखा', 'Meena': 'मीना', 'Mina': 'मीना',
        'Leela': 'लीला', 'Lila': 'लीला', 'Usha': 'उषा', 'Sunita': 'सुनीता',
        'Anita': 'अनिता', 'Geeta': 'गीता', 'Gita': 'गीता', 'Seeta': 'सीता',
        'Sita': 'सीता', 'Radha': 'राधा', 'Kavita': 'कविता', 'Savita': 'सविता',
        'Anjali': 'अंजली', 'Shweta': 'श्वेता', 'Sweta': 'श्वेता', 'Bhavna': 'भावना',
        'Pratima': 'प्रतिमा', 'Pratiksha': 'प्रतीक्षा', 'Madhuri': 'माधुरी',
        'Kalpana': 'कल्पना', 'Alka': 'अलका', 'Shanti': 'शांती', 'Pallavi': 'पल्लवी',
        'Deepa': 'दीपा', 'Dipa': 'दीपा', 'Jyoti': 'ज्योती', 'Manisha': 'मनिषा',
        'Nisha': 'निशा', 'Rita': 'रीता', 'Mona': 'मोना', 'Seema': 'सीमा',
        'Roshni': 'रोशनी', 'Komal': 'कोमल', 'Priti': 'प्रीती', 'Asha': 'आशा',
        // Common words
        'Sai': 'साई', 'Vaidik': 'वैदिक', 'Vivah': 'विवाह', 'President': 'अध्यक्ष',
        'Shree': 'श्री', 'Shri': 'श्री', 'Saibaba': 'साईबाबा',
        // Place names
        'Nashik': 'नाशिक', 'nashik': 'नाशिक', 'NASHIK': 'नाशिक',
        'Mumbai': 'मुंबई', 'Pune': 'पुणे', 'Nagpur': 'नागपूर',
        'Kolhapur': 'कोल्हापूर', 'Solapur': 'सोलापूर', 'Aurangabad': 'औरंगाबाद',
        'Thane': 'ठाणे', 'Satara': 'सातारा', 'Ratnagiri': 'रत्नागिरी',
        'Jalgaon': 'जळगाव', 'Ahmednagar': 'अहमदनगर', 'Sangli': 'सांगली',
        'Dhule': 'धुळे', 'dhule': 'धुळे', 'Dhulia': 'धुळे',
        'Malegaon': 'मालेगाव', 'Nasik': 'नाशिक',
    };

    // Check if it's a known name
    if (commonNames[text]) return commonNames[text];

    // Basic transliteration for unknown names
    return basicTransliterate(text);
};

// Basic transliteration rules
const basicTransliterate = (text) => {
    if (!text) return '';

    // Vowel mappings
    const vowels = {
        'a': 'अ', 'aa': 'आ', 'A': 'अ', 'AA': 'आ',
        'i': 'इ', 'ee': 'ई', 'I': 'इ', 'II': 'ई',
        'u': 'उ', 'oo': 'ऊ', 'U': 'उ', 'UU': 'ऊ',
        'e': 'ए', 'E': 'ए', 'ai': 'ऐ',
        'o': 'ओ', 'O': 'ओ', 'au': 'औ', 'ou': 'औ'
    };

    // Consonant mappings
    const consonants = {
        'k': 'क', 'kh': 'ख', 'g': 'ग', 'gh': 'घ',
        'c': 'च', 'ch': 'छ', 'j': 'ज', 'jh': 'झ',
        'T': 'ट', 'Th': 'ठ', 'D': 'ड', 'Dh': 'ढ', 'N': 'ण',
        't': 'त', 'th': 'थ', 'd': 'द', 'dh': 'ध', 'n': 'न',
        'p': 'प', 'f': 'फ', 'ph': 'फ', 'b': 'ब', 'bh': 'भ', 'm': 'म',
        'y': 'य', 'r': 'र', 'l': 'ल', 'v': 'व', 'w': 'व',
        'sh': 'श', 's': 'स', 'h': 'ह',
        'K': 'क', 'Kh': 'ख', 'G': 'ग', 'Gh': 'घ',
        'C': 'च', 'Ch': 'छ', 'J': 'ज', 'Jh': 'झ',
        'S': 'स', 'Sh': 'श', 'R': 'र', 'L': 'ल', 'M': 'म', 'Y': 'य', 'V': 'व', 'W': 'व', 'H': 'ह'
    };

    // Matra (vowel signs)
    const matras = {
        'a': '', 'aa': 'ा', 'i': 'ि', 'ee': 'ी', 'u': 'ु', 'oo': 'ू',
        'e': 'े', 'ai': 'ै', 'o': 'ो', 'au': 'ौ', 'ou': 'ौ'
    };

    let result = '';
    let i = 0;
    const len = text.length;

    while (i < len) {
        let matched = false;

        for (let j = Math.min(4, len - i); j >= 1; j--) {
            const substr = text.substring(i, i + j);

            if (consonants[substr]) {
                if (i + j < len) {
                    const nextChar = text[i + j];
                    if (matras[nextChar] !== undefined) {
                        result += consonants[substr] + matras[nextChar];
                        i += j + 1;
                        matched = true;
                        break;
                    } else if (nextChar === 'a' || nextChar === 'A') {
                        result += consonants[substr];
                        i += j + 1;
                        matched = true;
                        break;
                    }
                }
                result += consonants[substr];
                i += j;
                matched = true;
                break;
            }

            if (vowels[substr]) {
                result += vowels[substr];
                i += j;
                matched = true;
                break;
            }
        }

        if (!matched) {
            result += text[i];
            i++;
        }
    }

    return result;
};

const VerifyCertificate = () => {
    const { certificateNo: urlCertNo } = useParams();
    const { t } = useLanguage();
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (urlCertNo) {
            verifyCertificate(urlCertNo);
        }
    }, [urlCertNo]);

    const verifyCertificate = async (certNo) => {
        setLoading(true);
        setResult(null);
        setError(null);

        try {
            const response = await verificationAPI.verify(certNo);
            setResult(response.data);
        } catch (err) {
            if (err.response?.status === 404) {
                setError(t('verification.notFound') || 'Certificate not found');
            } else {
                setError('Failed to verify certificate');
            }
        } finally {
            setLoading(false);
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="inline-block w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4" />
                    <p className="text-gray-600 text-lg">Verifying certificate...</p>
                </div>
            </div>
        );
    }

    // Error / Not Found
    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="bg-white shadow-xl rounded-xl p-8 max-w-md text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                        <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-red-800 mb-2">Certificate Not Found</h2>
                    <p className="text-gray-500">{error}</p>
                </div>
            </div>
        );
    }

    // No cert number in URL
    if (!result && !loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="bg-white shadow-xl rounded-xl p-8 max-w-md text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                        <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Certificate Verification</h2>
                    <p className="text-gray-500">Scan the QR code on a certificate to verify its authenticity.</p>
                </div>
            </div>
        );
    }

    const cert = result.certificate;
    const data = cert; // Alias for consistency with CertificatePrint

    // Build full image URLs from relative paths
    const imgUrl = (path) => {
        if (!path) return null;
        if (path.startsWith('http') || path.startsWith('data:') || path.startsWith('blob:')) return path;
        return `${STORAGE_URL}/${path}`;
    };

    // Styles matching CertificatePrint.jsx exactly
    const styles = {
        certificate: {
            position: 'relative',
            boxSizing: 'border-box',
            width: '100%',
            background: '#ffffff',
            fontFamily: '"Noto Sans Devanagari", "NotoSansDevanagari", sans-serif',
        },
        borderOuter: {
            border: '3px solid #b8860b',
            padding: '2mm',
            background: '#ffffff',
        },
        borderInner: {
            border: '1px solid #d4a843',
            padding: '3mm 4mm',
            background: '#ffffff',
        },
        topBand: {
            textAlign: 'center',
            border: 'none',
            padding: 0,
            background: '#ffffff',
        },
        titleMarathi: {
            fontSize: '17px',
            fontWeight: 700,
            color: '#1a3a6b',
            lineHeight: 1.15,
            marginTop: 0,
            fontFamily: '"Noto Sans Devanagari", "NotoSansDevanagari", sans-serif',
        },
        titleEnglish: {
            fontSize: '17px',
            fontWeight: 700,
            color: '#1a3a6b',
            letterSpacing: '1px',
            marginTop: '3px',
            marginBottom: '4px',
            fontFamily: '"Noto Sans Devanagari", "NotoSansDevanagari", sans-serif',
        },
        sub: {
            fontSize: '11px',
            color: '#1a3a6b',
            marginTop: '1px',
            fontFamily: '"Noto Sans Devanagari", "NotoSansDevanagari", sans-serif',
        },
        regNo: {
            fontSize: '11px',
            color: '#1a3a6b',
            fontWeight: 'bold',
            marginTop: '1px',
            fontFamily: '"Noto Sans Devanagari", "NotoSansDevanagari", sans-serif',
        },
        centerDivider: {
            width: '240px',
            margin: '4px auto',
            borderTop: '1px solid #d4a843',
        },
        goldDivider: {
            height: '1px',
            background: '#d4a843',
            margin: '2px 0',
        },
        certNumRow: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '10px',
            color: '#1a3a6b',
            padding: '3px 0',
            borderBottom: '0.5px solid #e0d5b5',
            fontWeight: 'bold'
        },
        photoTable: {
            width: '100%',
            borderCollapse: 'collapse',
            margin: '3px 0',
        },
        photoCell: {
            width: '105px',
            textAlign: 'center',
            verticalAlign: 'top',
        },
        photoContainer: {
            position: 'relative',
            width: '75px',
            height: '95px',
            margin: '0 auto',
        },
        photoFrame: {
            width: '75px',
            height: '95px',
            border: '1.5px solid #9c7a2e',
            background: '#ffffff',
            textAlign: 'center',
            lineHeight: '95px',
            overflow: 'hidden',
        },
        stampOnPhotoRight: {
            position: 'absolute',
            width: '58px',
            height: '58px',
            right: '-29px',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 999,
        },
        stampOnPhotoLeft: {
            position: 'absolute',
            width: '58px',
            height: '58px',
            left: '-29px',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 999,
        },
        photoCaption: {
            fontSize: '10px',
            color: '#1a3a6b',
            marginTop: '2px',
            fontFamily: '"Noto Sans Devanagari", "NotoSansDevanagari", sans-serif',
        },
        marathiBlock: {
            fontSize: '10px',
            lineHeight: 1.25,
            padding: '5px 7px',
            color: '#1a3a6b',
            border: '0.5px solid #d4a843',
            background: '#ffffff',
            fontFamily: '"Noto Sans Devanagari", "NotoSansDevanagari", sans-serif',
        },
        // Field row styles
        fieldRow: {
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'baseline',
            marginBottom: '2px',
            gap: '2px',
        },
        labelText: {
            fontSize: '10px',
            color: '#1a3a6b',
            fontFamily: '"Noto Sans Devanagari", "NotoSansDevanagari", sans-serif',
        },
        labelInline: {
            marginLeft: '8px',
        },
        flWide: {
            color: '#1a3a6b',
            borderBottom: '1px solid #9c7a2e',
            display: 'inline-block',
            minWidth: '120px',
            fontWeight: 'bold',
            fontFamily: '"Noto Sans Devanagari", "NotoSansDevanagari", sans-serif',
            paddingBottom: '1px',
        },
        flWideO: {
            color: '#1a3a6b',
            borderBottom: '1px solid #9c7a2e',
            display: 'inline-block',
            minWidth: '100px',
            fontWeight: 'bold',
            fontFamily: '"Noto Sans Devanagari", "NotoSansDevanagari", sans-serif',
            paddingBottom: '1px',
        },
        flShort: {
            color: '#1a3a6b',
            borderBottom: '1px solid #9c7a2e',
            display: 'inline-block',
            minWidth: '35px',
            fontWeight: 'bold',
            fontFamily: '"Noto Sans Devanagari", "NotoSansDevanagari", sans-serif',
            paddingBottom: '1px',
            textAlign: 'center',
        },
        fl: {
            color: '#1a3a6b',
            borderBottom: '1px solid #9c7a2e',
            display: 'inline-block',
            minWidth: '45px',
            fontWeight: 'bold',
            fontFamily: '"Noto Sans Devanagari", "NotoSansDevanagari", sans-serif',
            paddingBottom: '1px',
        },
        textLine: {
            fontSize: '10px',
            lineHeight: 1.5,
            color: '#1a3a6b',
            fontFamily: '"Noto Sans Devanagari", "NotoSansDevanagari", sans-serif',
        },
        engLine: {
            fontFamily: 'Georgia, "Times New Roman", serif',
        },
        // Bottom table styles
        bottomTable: {
            width: '100%',
            borderCollapse: 'collapse',
            marginTop: '5px',
            tableLayout: 'fixed',
            background: '#ffffff',
        },
        detailLabel: {
            fontSize: '8px',
            color: '#1a3a6b',
            fontFamily: '"Noto Sans Devanagari", "NotoSansDevanagari", sans-serif',
        },
        detailValue: {
            borderBottom: '1px solid #9c7a2e',
            minWidth: '65px',
            fontWeight: 'bold',
            fontSize: '10px',
            display: 'inline-block',
            fontFamily: '"Noto Sans Devanagari", "NotoSansDevanagari", sans-serif',
            color: '#1a3a6b',
        },
        qrBox: {
            width: '60px',
            height: '60px',
            border: '1.5px solid #9c7a2e',
            background: '#ffffff',
            marginTop: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        qrLabel: {
            fontSize: '7px',
            color: '#1a3a6b',
            marginTop: '1px',
        },
        stampBox: {
            width: '65px',
            height: '65px',
            margin: '0 auto 3px',
            overflow: 'hidden',
            background: 'transparent',
        },
        stampPlaceholder: {
            width: '65px',
            height: '65px',
            margin: '0 auto 3px',
            border: '2px dashed #9c7a2e',
            borderRadius: '50%',
            textAlign: 'center',
            lineHeight: '65px',
            fontSize: '8px',
            color: '#bbb',
            fontStyle: 'italic',
        },
        sigRow: {
            fontSize: '10px',
            color: '#1a3a6b',
            marginBottom: '2px',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            fontFamily: '"Noto Sans Devanagari", "NotoSansDevanagari", sans-serif',
        },
        sigLineInline: {
            width: '90px',
            height: '44px',
            borderBottom: '1.5px solid #9c7a2e',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        sigCaptionBottom: {
            fontSize: '9px',
            color: '#1a3a6b',
            fontWeight: 600,
            fontFamily: '"Noto Sans Devanagari", "NotoSansDevanagari", sans-serif',
        },
        authorityInfo: {
            fontSize: '8px',
            color: '#1a3a6b',
            fontStyle: 'italic',
            lineHeight: 1.3,
            fontFamily: '"Noto Sans Devanagari", "NotoSansDevanagari", sans-serif',
        },
        bottomBand: {
            marginTop: '6px',
            background: '#ffffff',
            borderTop: '1px solid #d4a843',
            borderBottom: '2px solid #d4a843',
            borderLeft: '2px solid #d4a843',
            borderRight: '2px solid #d4a843',
        },
        addressText: {
            fontSize: '8px',
            color: '#1a3a6b',
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
            fontWeight: 600,
            fontFamily: '"Noto Sans Devanagari", "NotoSansDevanagari", sans-serif',
            textAlign: 'center',
            padding: '3px 0',
        },
    };

    // Valid certificate display
    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-3xl mx-auto px-4">

                {/* Valid Badge */}
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3 mb-6">
                    <svg className="w-8 h-8 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-lg font-semibold text-green-800">
                        {t('verification.valid') || 'Certificate is Valid'}
                    </span>
                </div>

                {/* Certificate Card - Matching CertificatePrint.jsx exactly */}
                <div style={styles.certificate}>
                    <div style={styles.borderOuter}>
                        <div style={styles.borderInner}>

                            {/* Top Band */}
                            <div style={styles.topBand}>
                                <div style={styles.titleMarathi}>विवाह प्रमाणपत्र</div>
                                <div style={styles.sub}>हिंदू विवाह कायदा १९५५ (कलम ०७ पहा)</div>
                                <div style={styles.regNo}>रजि. नं. २०२०६००३१४०३३९०० </div>
                                <div style={styles.centerDivider}></div>
                                <div style={styles.titleEnglish}>CERTIFICATE OF MARRIAGE</div>
                                <div style={styles.sub}>Hindu Marriage Act 1955 (See Section 07)</div>
                                <div style={styles.regNo}>Reg. No. 2020600314033900</div>
                            </div>

                            <div style={styles.goldDivider}></div>

                            {/* Certificate Number */}
                            {/* <div style={styles.certNumRow}>
                                <span>प्रमाणपत्र क्र. <strong>{toMarathiNumerals(cert.certificate_no)}</strong></span>
                                <span>Cert No. <strong>{cert.certificate_no}</strong></span>
                            </div> */}

                            {/* Photos Row */}
                            <table style={styles.photoTable}>
                                <tbody>
                                    <tr>
                                        {/* Groom Photo */}
                                        <td style={styles.photoCell}>
                                            <div style={styles.photoContainer}>
                                                <div style={styles.photoFrame}>
                                                    {cert.husband_photo ? (
                                                        <img src={imgUrl(cert.husband_photo)} alt="Groom" style={{ width: '75px', height: '95px', objectFit: 'cover' }} />
                                                    ) : (
                                                        <span style={{ fontSize: '8px', color: '#aaa', fontStyle: 'italic', textAlign: 'center', display: 'inline-block', verticalAlign: 'middle', lineHeight: '1.3' }}>वर<br/>Groom</span>
                                                    )}
                                                </div>
                                                {cert.stamp && (
                                                    <div style={styles.stampOnPhotoRight}>
                                                        <img src={imgUrl(cert.stamp)} alt="Stamp" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                                    </div>
                                                )}
                                            </div>
                                            <div style={styles.photoCaption}>वर / Groom</div>
                                        </td>

                                        {/* Bride Photo */}
                                        <td style={styles.photoCell}>
                                            <div style={styles.photoContainer}>
                                                <div style={styles.photoFrame}>
                                                    {cert.wife_photo ? (
                                                        <img src={imgUrl(cert.wife_photo)} alt="Bride" style={{ width: '75px', height: '95px', objectFit: 'cover' }} />
                                                    ) : (
                                                        <span style={{ fontSize: '8px', color: '#aaa', fontStyle: 'italic', textAlign: 'center', display: 'inline-block', verticalAlign: 'middle', lineHeight: '1.3' }}>वधू<br/>Bride</span>
                                                    )}
                                                </div>
                                                {cert.stamp && (
                                                    <div style={styles.stampOnPhotoLeft}>
                                                        <img src={imgUrl(cert.stamp)} alt="Stamp" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                                    </div>
                                                )}
                                            </div>
                                            <div style={styles.photoCaption}>वधू / Bride</div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            {/* Certificate Body - Exact copy of CertificatePrint.jsx */}
                            <div style={{ padding: '2px 0', background: '#ffffff' }}>
                                <div style={styles.marathiBlock}>
                                    {/* Marathi Section */}
                                    <div style={{ fontWeight: 700, color: '#1a3a6b', marginBottom: '3px' }}>प्रमाणित करण्यात येते की,</div>

                                    <div style={styles.fieldRow}>
                                        <span style={styles.labelText}>पतीचे नाव :</span>
                                        <span style={styles.flWideO}>{getMarathiText(cert, 'husband_name')}</span>
                                        <span style={{ ...styles.labelText, ...styles.labelInline }}>वय :</span>
                                        <span style={styles.flShort}>{toMarathiNumerals(cert.husband_age)} वर्षे</span>
                                    </div>

                                    <div style={styles.fieldRow}>
                                        <span style={styles.labelText}>राहणार :</span>
                                        <span style={styles.flWide}>{getMarathiText(cert, 'husband_address')}</span>
                                    </div>

                                    <div style={styles.fieldRow}>
                                        <span style={styles.labelText}>आणि पत्नीचे नाव :</span>
                                        <span style={styles.flWide}>{getMarathiText(cert, 'wife_name')}</span>
                                        <span style={{ ...styles.labelText, ...styles.labelInline }}>वय :</span>
                                        <span style={styles.flShort}>{toMarathiNumerals(cert.wife_age)} वर्षे</span>
                                    </div>

                                    <div style={styles.fieldRow}>
                                        <span style={styles.labelText}>राहणार :</span>
                                        <span style={styles.flWide}>{getMarathiText(cert, 'wife_address')}</span>
                                    </div>

                                    <div style={styles.textLine}>
                                        यांचा विवाह दिनांक :
                                        <span style={styles.flShort}>{toMarathiDateShort(cert.marriage_date)}</span> रोजी {getMarathiText(cert, 'office_name')}, {getMarathiText(cert, 'place')} या ठिकाणी विधी संपन्न झाला. त्याची
                                        महाराष्ट्र विवाह मंडळाचे विनियमन आणि विवाह नोंदणी विधेयक, १९९८ अन्वये ठेवण्यात आलेल्या नोंदवहीच्या खंड
                                        क्रमांक : <span style={styles.flShort}>{toMarathiNumerals(cert.volume_no)}</span> च्या पृष्ठक्रमांक: <span style={styles.flShort}>{toMarathiNumerals(cert.serial_no)}</span> संस्थेकडून हिंदु वैदिक पध्दतीने (हिंदू विवाह कायदा १९५५, कलम ७ नुसार) विवाह संपन्न केला आहे.
                                    </div>

                                    <hr style={{ margin: '5px 0', border: 'none', borderTop: '1px solid #c9971f' }} />

                                    {/* English Section */}
                                    <div style={{ ...styles.engLine, fontWeight: 700, color: '#1a3a6b', marginBottom: '3px' }}>Certified that Marriage Between,</div>

                                    <div style={{ ...styles.fieldRow, ...styles.engLine }}>
                                        <span style={styles.labelText}>Husband's Name:</span>
                                        <span style={styles.flWideO}>{cert.husband_name}</span>
                                        <span style={{ ...styles.labelText, ...styles.labelInline }}>Age :</span>
                                        <span style={styles.flShort}>{cert.husband_age ? `${cert.husband_age} years` : '-'}</span>
                                    </div>

                                    <div style={{ ...styles.fieldRow, ...styles.engLine }}>
                                        <span style={styles.labelText}>Residing Ad.:</span>
                                        <span style={styles.flWideO}>{cert.husband_address || '-'}</span>
                                    </div>

                                    <div style={{ ...styles.fieldRow, ...styles.engLine }}>
                                        <span style={styles.labelText}>and Wife's Name:</span>
                                        <span style={styles.flWideO}>{cert.wife_name}</span>
                                        <span style={{ ...styles.labelText, ...styles.labelInline }}>Age :</span>
                                        <span style={styles.flShort}>{cert.wife_age ? `${cert.wife_age} years` : '-'}</span>
                                    </div>

                                    <div style={{ ...styles.fieldRow, ...styles.engLine }}>
                                        <span style={styles.labelText}>Residing Ad.:</span>
                                        <span style={styles.flWideO}>{cert.wife_address || '-'}</span>
                                    </div>

                                    <div style={{ ...styles.textLine, ...styles.engLine }}>
                                        Solemnized on
                                        <span style={styles.flShort}>{cert.marriage_date ? formatDate(cert.marriage_date) : '-'}</span>
                                        at (Place) {cert.office_name || 'Shree Sai Vaidik Vivah'}, {cert.place || 'Nashik'} of Volume No. <span style={{ ...styles.fl, minWidth: '50px' }}>{cert.volume_no || '-'} </span> Serial No. <span style={{ ...styles.fl, minWidth: '50px' }}>{cert.serial_no || '-'} </span> of Registered of Marriages Maintained
                                        under the Maharashtra Regulation of Marriage Bureaus and Registration of Marriages Act
                                        1998, Performed under Section 7 of Hindu Marriage Act 1955.
                                    </div>
                                </div>
                            </div>

                            {/* Bottom Table */}
                            <table style={styles.bottomTable}>
                                <tbody>
                                    <tr>
                                        {/* Left: Place, Date, QR */}
                                        <td style={{ width: '30%', padding: '0 4px', verticalAlign: 'top' }}>
                                            <div style={{ marginBottom: '4px' }}>
                                                <span style={styles.detailLabel}>ठिकाण: </span>
                                                <span style={{ ...styles.detailValue, fontFamily: '"NotoSansDevanagari", sans-serif' }}>
                                                    {getMarathiText(cert, 'place')}
                                                </span>
                                            </div>
                                            <div style={{ marginBottom: '4px' }}>
                                                <span style={styles.detailLabel}>Place: </span>
                                                <span style={styles.detailValue}>{cert.place || '-'}</span>
                                            </div>
                                            <div style={{ marginBottom: '4px' }}>
                                                <span style={styles.detailLabel}>दिनांक: </span>
                                                {cert.certificate_date && (
                                                    <span style={{ ...styles.detailValue, fontFamily: '"NotoSansDevanagari", sans-serif' }}>
                                                        {toMarathiDateShort(cert.certificate_date)}
                                                    </span>
                                                )}
                                            </div>
                                            <div style={{ marginBottom: '6px' }}>
                                                <span style={styles.detailLabel}>Date: </span>
                                                <span style={styles.detailValue}>
                                                    {cert.certificate_date ? formatDate(cert.certificate_date) : '-'}
                                                </span>
                                            </div>
                                            <div>
                                                <div style={styles.qrBox}>
                                                    <QRCodeSVG
                                                        value={window.location.origin + '/verify/' + cert.certificate_no}
                                                        size={55}
                                                        level="H"
                                                    />
                                                </div>
                                                <div style={styles.qrLabel}>स्कॅन करा / Scan to Verify</div>
                                            </div>
                                        </td>

                                        {/* Middle: Signatures */}
                                        <td style={{ width: '35%', padding: '0 4px', paddingTop: '4px' }}>
                                            <div style={styles.sigRow}>
                                                वर / Groom
                                                <span style={styles.sigLineInline}>
                                                    {cert.husband_signature && (
                                                        <img src={imgUrl(cert.husband_signature)} alt="Groom Signature" style={{ maxHeight: '44px', objectFit: 'contain' }} />
                                                    )}
                                                </span>
                                            </div>
                                            <div style={styles.sigRow}>
                                                वधू / Bride
                                                <span style={styles.sigLineInline}>
                                                    {cert.wife_signature && (
                                                        <img src={imgUrl(cert.wife_signature)} alt="Bride Signature" style={{ maxHeight: '44px', objectFit: 'contain' }} />
                                                    )}
                                                </span>
                                            </div>
                                        </td>

                                        {/* Right: Authority */}
                                        <td style={{ width: '35%', textAlign: 'center', verticalAlign: 'top', padding: '0 4px' }}>
                                            {cert.stamp ? (
                                                <div style={styles.stampBox}>
                                                    <img src={imgUrl(cert.stamp)} alt="Stamp" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                                                </div>
                                            ) : (
                                                <div style={styles.stampPlaceholder}>
                                                    Official<br/>Stamp
                                                </div>
                                            )}
                                            <div style={{ height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '2px' }}>
                                                {cert.authority_signature && (
                                                    <img src={imgUrl(cert.authority_signature)} alt="Authority Signature" style={{ maxHeight: '20px', maxWidth: '80px', objectFit: 'contain' }} />
                                                )}
                                            </div>
                                            <div style={styles.sigCaptionBottom}>Signature</div>
                                            <div style={styles.authorityInfo}>
                                                <div style={{ fontWeight: 600, fontSize: '10px' }}>{cert.authority_name || 'President'}</div>
                                                {cert.office_name && <div style={{ fontSize: '9px', fontWeight: 600 }}>{cert.office_name}</div>}
                                                {cert.office_address && <div style={{ fontSize: '8px' }}>{cert.office_address}</div>}
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            {/* Footer Band */}
                            <div style={styles.bottomBand}>
                                <div style={styles.addressText}>
                                    {cert.office_name || 'Shree Sai Vaidik Vivah'}, {cert.office_address || 'Ashok Stamb, Nashik'}
                                </div>
                            </div>

                        </div>
                    </div>
                </div>

                <p className="text-center text-sm text-gray-400 mt-6">
                    shreesaivaidik.com
                </p>
            </div>
        </div>
    );
};

export default VerifyCertificate;